import { Helmet } from "react-helmet-async";

export default function Privatlivspolitik() {
  return (
    <div className="bg-white min-h-screen pt-32 pb-24">
      <Helmet>
        <title>Privatlivspolitik – Ta' JA-Hatten På!</title>
        <meta 
          name="description" 
          content="Læs vores privatlivspolitik for at se, hvordan vi beskytter, behandler og opbevarer dine personlige oplysninger fortroligt og sikkert." 
        />
      </Helmet>

      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        {/* Header */}
        <div className="border-b border-slate-100 pb-12 mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-red-600 font-bold mb-4">Sikkerhed & Data</p>
          <h1 className="text-4xl md:text-5xl font-normal text-slate-900 tracking-tight leading-tight mb-6">
            Privatlivspolitik
          </h1>
          <p className="text-slate-500 text-sm">
            Senest opdateret: 3. juni 2026
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-12 text-slate-600 leading-relaxed font-light">
          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">1. Generelt om vores privatlivspolitik</h2>
            <p>
              Hos <strong>Ta' Ja-Hatten På!</strong> (CVR: 46313291) prioriterer vi datasikkerhed og fortrolighed højt. Denne privatlivspolitik beskriver, hvordan vi indsamler, beskytter og behandler de personoplysninger, du efterlader og/eller afgiver, når du besøger vores hjemmeside og benytter vores ydelser, herunder vores kontaktformular.
            </p>
            <p>
              Vi sikrer, at dine personoplysninger behandles i overensstemmelse med gældende lovgivning, herunder EU's persondataforordning (GDPR) samt den danske databeskyttelseslov.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">2. Dataansvarlig</h2>
            <p>
              Den dataansvarlige for indsamling, behandling og anvendelse af dine personoplysninger på denne hjemmeside er:
            </p>
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-6 space-y-2 text-sm font-normal text-slate-700">
              <p className="font-bold text-slate-900">Ta' Ja-Hatten På!</p>
              <p>Ejer: Candra Gaarde</p>
              <p>CVR-nummer: 46313291</p>
              <p>E-mail: <a href="mailto:Kontakt@ja-hatten.dk" className="text-red-600 hover:underline">Kontakt@ja-hatten.dk</a></p>
              <p>Telefon: <a href="tel:+4522290935" className="text-red-600 hover:underline">22 29 09 35</a></p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">3. Hvilke oplysninger indsamler vi, og hvorfor?</h2>
            <p>
              Vi indsamler udelukkende de personoplysninger, som er nødvendige for at kunne levere den bedst mulige service og besvare dine henvendelser.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Kontaktformular:</strong> Når du kontakter os via formularen på hjemmesiden, indsamler vi dit navn, din e-mailadresse, eventuelt telefonnummer samt indholdet af din besked. Formålet er at kunne besvare din henvendelse og give dig et uforpligtende tilbud på foredrag eller rådgivning.
              </li>
              <li>
                <strong>Brugsdata (Cookies):</strong> Vi anvender basale, anonymiserede cookies for at optimere hjemmesidens funktionalitet og brugeroplevelse. Disse data kan ikke henføres til dig som enkeltperson.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">4. Retsgrundlag for behandlingen</h2>
            <p>
              Behandlingen af dine personoplysninger sker på baggrund af:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Samtykke (GDPR art. 6, stk. 1, litra a):</strong> Når du udfylder vores kontaktformular, giver du dit udtrykkelige samtykke til, at vi må behandle de indtastede oplysninger for at kunne kontakte dig.
              </li>
              <li>
                <strong>Opfyldelse af kontrakt (GDPR art. 6, stk. 1, litra b):</strong> Hvis din henvendelse fører til en aftale om booking af et foredrag eller en workshop, behandler vi dine data for at kunne opfylde denne aftale.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">5. Videregivelse af oplysninger</h2>
            <p>
              Dine personoplysninger bliver under ingen omstændigheder solgt, udlejet eller overdraget til tredjemand med henblik på markedsføring.
            </p>
            <p>
              Vi kan overlade dine personoplysninger til betroede databehandlere (f.eks. vores hosting- og e-mailudbydere), som udelukkende behandler oplysningerne på vores vegne og i overensstemmelse med vores instruktioner samt gældende databehandleraftaler.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">6. Opbevaring og sletning</h2>
            <p>
              Vi opbevarer kun dine personoplysninger, så længe det er nødvendigt for at opfylde de formål, de blev indsamlet til.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Henvendelser via kontaktformularen, der ikke fører til et samarbejde eller booking, slettes som udgangspunkt senest 6 måneder efter endt dialog.
              </li>
              <li>
                Oplysninger i forbindelse med bookinger og fakturering opbevares i op til 5 år i overensstemmelse med bogføringslovens krav, hvorefter de slettes permanent.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">7. Dine rettigheder</h2>
            <p>
              Du har som registreret en række rettigheder i forhold til vores behandling af dine personoplysninger:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Ret til indsigt:</strong> Du har ret til at få oplyst, hvilke personoplysninger vi behandler om dig.</li>
              <li><strong>Ret til berigtigelse:</strong> Du har ret til at få rettet forkerte eller ufuldstændige oplysninger.</li>
              <li><strong>Ret til sletning ("retten til at blive glemt"):</strong> Du har i visse tilfælde ret til at få slettet dine oplysninger før vores normale slettefrist.</li>
              <li><strong>Ret til begrænsning:</strong> Du har i visse tilfælde ret til at få begrænset behandlingen af dine oplysninger.</li>
              <li><strong>Ret til at trække samtykke tilbage:</strong> Du kan til enhver tid trække dit samtykke tilbage ved at kontakte os på <a href="mailto:Kontakt@ja-hatten.dk" className="text-red-600 hover:underline">Kontakt@ja-hatten.dk</a>.</li>
            </ul>
          </section>

          <section className="space-y-4 pb-8">
            <h2 className="text-2xl font-serif italic text-slate-900 font-normal">8. Klage til Datatilsynet</h2>
            <p>
              Hvis du er utilfreds med den måde, vi behandler dine personoplysninger på, har du ret til at indgive en klage til Datatilsynet. Vi opfordrer dig dog til altid at kontakte os først, så vi kan finde en fælles løsning.
            </p>
            <p className="text-sm pt-4">
              Datatilsynet • Carl Jacobsens Vej 35 • 2500 Valby • E-mail: dt@datatilsynet.dk
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
